/*  
This component downloads the displayed table in pdf format. This component is called from Home.js file and rendered as part of service inventory 
page.

This downloaded pdf file is named as 'selected-environment-name_date'. This file has a title, last updated date and time of the contents of
the table, information about the selected environments, cluster, namespace and finally contents of the table. 
*/

import React from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './Download.css';
import { IoMdDownload } from "react-icons/io";

const Download = ({ environmentData, selectedEnvironment, selectedClusters, selectedNamespaces, environmentTime }) => {
  const generatePDF = () => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();

    const drawCacheTime = () => {
      doc.setFontSize(10);
      const timeText = `Date and Time: ${environmentTime || 'None'}`;
      const timeWidth = doc.getStringUnitWidth(timeText) * 10 / doc.internal.scaleFactor;
      doc.text(timeText, pageWidth - timeWidth - 10, 10);
    };

    const wrapText = (text, maxWidth) => {
      const words = text.split(', ');
      let lines = [];
      let currentLine = words[0];

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = doc.getStringUnitWidth(currentLine + ', ' + word) * doc.internal.getFontSize() / doc.internal.scaleFactor;
        
        if (width < maxWidth) {
          currentLine += ', ' + word;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      }
      lines.push(currentLine);
      return lines;
    };

    const drawFirstPageHeader = () => {
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      const title = 'Release Dashboard';
      const titleWidth = doc.getStringUnitWidth(title) * 16 / doc.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      doc.text(title, titleX, 20);

      doc.setFont(undefined, 'normal');
      doc.setFontSize(12);

      let yPosition = 35;

      // Environment
      if (selectedEnvironment) {
        doc.text(`Environment: ${selectedEnvironment}`, 14, yPosition);
        yPosition += 8;
      }

      // Clusters
      if (selectedClusters && selectedClusters.length > 0) {
        const maxWidth = pageWidth - 28; // Adjust based on page margins
        
        if (selectedClusters.includes('Select All')) {
          doc.text('Cluster: All Clusters', 14, yPosition);
          yPosition += 6;
          
          // Get all unique clusters from environmentData
          const allClusters = [...new Set(environmentData.map(item => item.cluster))].filter(Boolean).sort();
          
          if (allClusters.length > 0) {
            const wrappedClusters = wrapText(allClusters.join(', '), maxWidth);
            wrappedClusters.forEach(line => {
              doc.text(line, 14, yPosition);
              yPosition += 5;
            });
          }
          yPosition += 3;
        } else {
          doc.text(`Cluster: ${selectedClusters.join(', ')}`, 14, yPosition);
          yPosition += 8;
        }
      }

      // Namespaces
      if (selectedNamespaces && selectedNamespaces.length > 0) {
        const maxWidth = pageWidth - 28;
        
        if (selectedNamespaces.includes('Select All')) {
          doc.setFontSize(12);
          doc.text('Namespaces: All Namespaces', 14, yPosition);
          yPosition += 6;
          
          // Get all unique namespaces from environmentData
          const allNamespaces = [...new Set(environmentData.map(item => item.namespace))].filter(Boolean).sort();
          
          if (allNamespaces.length > 0) {
            const wrappedNamespaces = wrapText(allNamespaces.join(', '), maxWidth);
            wrappedNamespaces.forEach(line => {
              doc.text(line, 14, yPosition);
              yPosition += 5;
            });
          }
          yPosition += 3;
        } else {
          doc.text(`Namespaces: ${selectedNamespaces.join(', ')}`, 14, yPosition);
          yPosition += 8;
        }
      }

      return yPosition + 5;
    };

    let filteredData = environmentData || [];

    if (selectedClusters && selectedClusters.length > 0 && !selectedClusters.includes('Select All')) {
      filteredData = filteredData.filter(item => selectedClusters.includes(item.cluster));
    }

    if (selectedNamespaces && selectedNamespaces.length > 0 && !selectedNamespaces.includes('Select All')) {
      filteredData = filteredData.filter(item => 
        selectedNamespaces.some(ns => ns.trim().toLowerCase() === item.namespace.trim().toLowerCase())
      );
    }

    const data = filteredData.flatMap(item => {
      const mainContainers = Array.isArray(item['main-containers']) ? item['main-containers'] : [];

      if (mainContainers.length === 0) {
        return [{
          deploymentName: item['deployment-name'] || '-',
          namespace: item.namespace || '-',
          clusterName: item.cluster || '-',
          mainContainerImages: '-',
          version: '-',
          imageTag: '-'
        }];
      }

      return mainContainers.map(container => ({
        deploymentName: item['deployment-name'] || '-',
        namespace: item.namespace || '-',
        clusterName: item.cluster || '-',
        mainContainerImages: container.image ? container.image.split(':')[0] : '-',
        version: container.version || '-',
        imageTag: container.image_tag || '-'
      }));
    });

    const columns = [
      { header: 'Deployment Name', dataKey: 'deploymentName' },
      { header: 'Namespace', dataKey: 'namespace' },
      { header: 'Cluster Name', dataKey: 'clusterName' },
      { header: 'Main Container Images', dataKey: 'mainContainerImages' },
      { header: 'Version', dataKey: 'version' },
      { header: 'Image Tag', dataKey: 'imageTag' }
    ];

    drawCacheTime();
    const startY = drawFirstPageHeader();

    if (data.length === 0) {
      doc.setFontSize(12);
      doc.text("No data available", 14, startY + 10);
    } else {
      doc.autoTable({
        columns: columns,
        body: data,
        startY: startY,
        styles: {
          fontSize: 8,
          cellPadding: 3,
          lineWidth: 0.1,
          lineColor: [80, 80, 80]
        },
        columnStyles: {
          deploymentName: { cellWidth: 45 },
          namespace: { cellWidth: 35 },
          clusterName: { cellWidth: 35 },
          mainContainerImages: { cellWidth: 75, overflow: 'linebreak' },
          version: { cellWidth: 25 },
          imageTag: { cellWidth: 30 }
        },
        headStyles: {
          fillColor: [20, 66, 66],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'left'
        },
        bodyStyles: {
          halign: 'left'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        margin: { top: 25, left: 10, right: 10 },
        rowPageBreak: 'avoid',
        showFoot: 'lastPage',
        didDrawPage: (data) => {
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
          const totalPages = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.text(`Page ${pageNumber} of ${totalPages}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
          drawCacheTime();
        }
      });
    }

    const timestamp = new Date().toISOString().split('T')[0]; 
    const environmentName = selectedEnvironment ? selectedEnvironment.replace(/\s+/g, '_') : 'environment';
    const fileName = `${environmentName}_${timestamp}.pdf`;

    doc.save(fileName);
  };

  return (
    <button 
      className="download-button"
      onClick={generatePDF}
      disabled={!environmentData || environmentData.length === 0}
      data-tooltip="Download"
    >
      <IoMdDownload />
    </button>
  );
};

export default Download;